import { CANONICAL, GIFI, DEFAULT_DEPRECIATION_RATE_PERCENT, DEFAULT_DEPRECIATION_RATE_FRACTION } from "../../../../../shared/helpers/constants";

// CA_01..CA_06 — capital assets, depreciation, accumulated depreciation.
//
// Each entry is a cascade config. The orchestrator looks up step.id here and
// hands the config to the dispatcher.
//
// Adding a new CA-family cascade = add one entry. The custom-resolve escape
// hatches at the bottom are for the few cascades that need to read a peer
// step's data field or parse $ out of a detail string — patterns too specific
// to deserve their own type.

// ─── Custom resolvers (escape hatches for shapes the dispatcher doesn't model) ─

// CA_01: estimate depreciation as DEFAULT_DEPRECIATION_RATE_PERCENT% of gross
// PP&E from the peer step.
function resolveComputeDepreciationEstimate(resolved, ctx) {
	let response = ctx.userResponses["assess_new_or_missing"];
	if (response === "new_acquisitions") {
		return { ...resolved, status: "not_applicable", detail: "No depreciation — assets are new acquisitions" };
	}
	if (response === "depreciation_missing" && (resolved.status === "blocked" || resolved.status === "not_started")) {
		let step1 = ctx.steps.find(function (s) { return s.id === "confirm_ppe_assets"; });
		let grossPPE = step1 && step1.data && step1.data.sum ? Math.abs(step1.data.sum) : 0;
		let estimated = Math.round(grossPPE * DEFAULT_DEPRECIATION_RATE_FRACTION);
		return { ...resolved, status: "completed", detail: "Estimated depreciation: $" + estimated.toLocaleString("en-CA") + " (" + DEFAULT_DEPRECIATION_RATE_PERCENT + "% default rate — no PP&E schedule available)", data: { totalEstimated: estimated } };
	}
	return resolved;
}

// CA_01: depreciation AJE uses the grossPPE + defaultRate proposal shape
// (AJEProposal lets the reviewer adjust the rate before approving).
function resolvePrepareDepreciationAje(resolved, ctx) {
	let response = ctx.userResponses["assess_new_or_missing"];
	if (response === "new_acquisitions") {
		return { ...resolved, status: "not_applicable", detail: "No AJE needed — assets are new acquisitions" };
	}
	if (response === "depreciation_missing" && (resolved.status === "blocked" || resolved.status === "waiting_for_agent")) {
		let step1 = ctx.steps.find(function (s) { return s.id === "confirm_ppe_assets"; });
		let grossPPE = step1 && step1.data && step1.data.sum ? Math.abs(step1.data.sum) : 0;
		return {
			...resolved, status: "ready_to_propose", detail: null,
			inline_proposal: { grossPPE: grossPPE, defaultRate: DEFAULT_DEPRECIATION_RATE_PERCENT, debitCanonicalCode: CANONICAL.DEPRECIATION_EXPENSE, debitDescription: "Amortization of tangible assets", debitGifi: GIFI.DEPRECIATION_EXPENSE, creditCanonicalCode: CANONICAL.ACCUMULATED_DEPRECIATION, creditDescription: "Accumulated amortization of machinery, equipment, furniture, and fixtures", creditGifi: GIFI.ACCUMULATED_DEPRECIATION, description: "Record estimated annual depreciation" },
		};
	}
	return resolved;
}

// CA_02 need_aje: same grossPPE + defaultRate shape, amount derived from
// nbv on the peer step.
function resolveCa02NeedAje(resolved, ctx) {
	let step1 = ctx.steps.find(function (s) { return s.id === "ca02_confirm_ppe"; });
	let nbv = step1 && step1.data ? step1.data.nbv : 0;
	return {
		...resolved, status: "ready_to_propose", detail: null,
		inline_proposal: {
			grossPPE: Math.abs(nbv), defaultRate: DEFAULT_DEPRECIATION_RATE_PERCENT,
			debitCanonicalCode: CANONICAL.DEPRECIATION_EXPENSE, debitDescription: "Amortization of tangible assets", debitGifi: GIFI.DEPRECIATION_EXPENSE,
			creditCanonicalCode: CANONICAL.ACCUMULATED_DEPRECIATION, creditDescription: "Accumulated amortization of machinery, equipment, furniture, and fixtures", creditGifi: GIFI.ACCUMULATED_DEPRECIATION,
			description: "Record current-year depreciation expense",
		},
	};
}

// CA_04 orphaned: amount is parsed out of the peer step's detail string
// (the peer step renders "$1,234" in human text, no numeric data field).
function resolveCa04Orphaned(resolved, ctx) {
	let step1 = ctx.steps.find(function (s) { return s.id === "ca04_confirm_accum"; });
	let detailMatch = step1 && step1.detail ? step1.detail.match(/\$([0-9,]+)/) : null;
	let accumAmount = detailMatch ? parseInt(detailMatch[1].replace(/,/g, "")) : 0;
	return {
		...resolved, status: "ready_to_propose", detail: null,
		inline_proposal: {
			amount: accumAmount,
			debitCanonicalCode: CANONICAL.ACCUMULATED_DEPRECIATION, debitDescription: "Accumulated amortization of machinery, equipment, furniture, and fixtures", debitGifi: GIFI.ACCUMULATED_DEPRECIATION,
			creditCanonicalCode: CANONICAL.RETAINED_EARNINGS, creditDescription: "Prior period adjustments", creditGifi: GIFI.PRIOR_PERIOD_ADJUSTMENTS,
			description: "Write off orphaned accumulated depreciation",
		},
	};
}

// ─── Registry ────────────────────────────────────────────────────────────────

export const capitalAssetCascades = {
	// CA_01 — depreciation completeness
	"assess_new_or_missing": {
		type: "labelResponse",
		labels: {
			new_acquisitions: "Confirmed: new acquisitions — no depreciation expected",
			depreciation_missing: "Confirmed: depreciation is missing and should be recorded",
		},
	},
	"compute_depreciation_estimate": { type: "custom", resolve: resolveComputeDepreciationEstimate },
	"prepare_depreciation_aje": { type: "custom", resolve: resolvePrepareDepreciationAje },

	// CA_02 — depreciation expense unmapped or missing
	"ca02_determine_reason": {
		type: "labelResponse",
		labels: {
			fully_depreciated: "Confirmed fully depreciated",
			expense_unmapped: "Depreciation expense mapped to wrong account",
			need_aje: "Depreciation needs to be recorded",
		},
	},
	"ca02_resolve": {
		type: "branch", of: "ca02_determine_reason",
		when: {
			"fully_depreciated": { type: "acknowledge", detail: "Acknowledged — assets are fully depreciated" },
			"expense_unmapped": {
				type: "pickAccount",
				keywords: ["depreciation", "amortization", "deprec", "amort"],
				target: { canonicalCode: CANONICAL.DEPRECIATION_EXPENSE, canonicalName: "Amortization of tangible assets" },
				question: "Which account should be mapped to Depreciation Expense?",
				foundNoun: "account(s) that may be depreciation expense",
			},
			"need_aje": { type: "custom", resolve: resolveCa02NeedAje },
		},
	},

	// CA_03 — depreciation without PP&E
	"ca03_determine_reason": {
		type: "labelResponse",
		labels: {
			ppe_unmapped: "PP&E exists but is unmapped",
			asset_disposed: "Assets disposed — partial year depreciation",
			expense_misclassified: "Expense should be reclassified",
		},
	},
	"ca03_resolve": {
		type: "branch", of: "ca03_determine_reason",
		when: {
			"asset_disposed": { type: "acknowledge", detail: "Acknowledged — assets disposed, depreciation is from partial period" },
			"ppe_unmapped": {
				type: "pickAccount",
				keywords: ["equipment", "furniture", "vehicle", "building", "computer", "capital", "asset", "leasehold"],
				target: { canonicalCode: CANONICAL.EQUIPMENT, canonicalName: "Machinery, equipment, furniture, and fixtures" },
				question: "Which account should be mapped to PP&E?",
			},
			"expense_misclassified": {
				type: "pickAccount",
				keywords: ["expense", "rent", "lease", "operating"],
				target: { canonicalCode: CANONICAL.OPERATING_EXPENSE, canonicalName: "Other expenses" },
				question: "Which expense account should this be reclassified to?",
			},
		},
	},

	// CA_04 — accumulated depreciation without PP&E
	"ca04_determine_reason": {
		type: "labelResponse",
		labels: {
			ppe_unmapped: "Gross PP&E exists but is unmapped",
			asset_disposed: "Assets disposed — remove both cost and accumulated depreciation",
			orphaned: "Orphaned balance — should be written off",
		},
	},
	"ca04_resolve": {
		type: "branch", of: "ca04_determine_reason",
		when: {
			"asset_disposed": { type: "acknowledge", detail: "Acknowledged — assets disposed, accumulated depreciation should be removed" },
			"orphaned": { type: "custom", resolve: resolveCa04Orphaned },
			"ppe_unmapped": {
				type: "pickAccount",
				keywords: ["equipment", "furniture", "vehicle", "building", "computer", "capital", "asset", "leasehold"],
				target: { canonicalCode: CANONICAL.EQUIPMENT, canonicalName: "Machinery, equipment, furniture, and fixtures" },
				question: "Which account should be mapped to PP&E?",
			},
		},
	},

	// CA_05 — accumulated depreciation overshoot (exceeds gross cost)
	"ca05_determine_cause": {
		type: "labelResponse",
		labels: {
			disposal_incomplete: "Disposal reduced cost but not accumulated depreciation",
			mapping_error: "Account mapped to wrong category",
			posting_error: "Posting error caused the imbalance",
		},
	},
	"ca05_resolve": {
		type: "branch", of: "ca05_determine_cause",
		when: {
			"disposal_incomplete": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ca05_show_numbers", amountField: "overshoot", abs: false,
				proposal: {
					debitCanonicalCode: CANONICAL.ACCUMULATED_DEPRECIATION, debitDescription: "Accumulated amortization of machinery, equipment, furniture, and fixtures", debitGifi: GIFI.ACCUMULATED_DEPRECIATION,
					creditCanonicalCode: CANONICAL.LOSS_ON_DISPOSAL, creditDescription: "Realized gains/losses on disposal of assets", creditGifi: GIFI.LOSS_ON_DISPOSAL,
					description: "Correct accumulated depreciation overshoot — incomplete disposal",
				},
			},
			"posting_error": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ca05_show_numbers", amountField: "overshoot", abs: false,
				proposal: {
					debitCanonicalCode: CANONICAL.ACCUMULATED_DEPRECIATION, debitDescription: "Accumulated amortization of machinery, equipment, furniture, and fixtures", debitGifi: GIFI.ACCUMULATED_DEPRECIATION,
					creditCanonicalCode: CANONICAL.DEPRECIATION_EXPENSE, creditDescription: "Amortization of tangible assets (correction)", creditGifi: GIFI.DEPRECIATION_EXPENSE,
					description: "Correct accumulated depreciation overshoot — posting error",
				},
			},
			"mapping_error": {
				type: "pickAccount",
				keywords: ["equipment", "furniture", "vehicle", "building", "capital", "depreciation", "accumulated"],
				target: { canonicalCode: CANONICAL.EQUIPMENT, canonicalName: "Machinery, equipment, furniture, and fixtures" },
				question: "Which account is misclassified?",
			},
		},
	},

	// CA_06 — depreciation expense without matching PP&E
	"ca06_determine_reason": {
		type: "labelResponse",
		labels: {
			depreciable_misclassified: "Depreciable assets misclassified",
			disposed_asset: "Depreciation from disposed asset",
			expense_misclassified: "Expense should be reclassified",
		},
	},
	"ca06_resolve": {
		type: "branch", of: "ca06_determine_reason",
		when: {
			"disposed_asset": { type: "acknowledge", detail: "Acknowledged — depreciation from disposed asset" },
			"depreciable_misclassified": {
				type: "pickAccount",
				keywords: ["equipment", "furniture", "vehicle", "building", "computer", "leasehold", "machinery"],
				target: { canonicalCode: CANONICAL.EQUIPMENT, canonicalName: "Machinery, equipment, furniture, and fixtures" },
				question: "Which account should be mapped as depreciable PP&E?",
			},
			"expense_misclassified": {
				type: "pickAccount",
				keywords: ["expense", "rent", "lease", "repair", "maintenance", "operating"],
				target: { canonicalCode: CANONICAL.OPERATING_EXPENSE, canonicalName: "Other expenses" },
				question: "Which account should this expense be reclassified to?",
			},
		},
	},
};
